import { getAnalyticsReportTabsSx } from '../../shared/report-design';
import { useMemo, useState } from 'react';
import { Box, Typography, Card, CardContent, Chip, Tab } from '@mui/material';
import ResponsiveTabs from '../../../system/responsive-tabs';
import { resolveShowCost } from '../../../system/report-visibility';
import { formatUsd } from '../../../system/report-formatters';

const TABS = [
  { label: 'Headlines', key: 0 },
  { label: 'Descriptions', key: 1 },
  { label: 'Final URLs', key: 2 },
  { label: 'Assets', key: 3 },
];

const formatNumber = (value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value || 0));
const formatMoney = (value) => formatUsd(Number(value || 0));
const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;

const normalizeTextItems = (source, label) => {
  const output = [];
  const push = (val) => {
    if (val == null) return;
    if (Array.isArray(val)) {
      val.forEach((item) => push(item));
      return;
    }
    if (typeof val === 'object') {
      const text = val.text ?? val.TEXT ?? val.value ?? val.name ?? val.label;
      const pinned = val.pinned ?? val.PINNED ?? '';
      if (text) {
        output.push({ text: String(text), pinned, label, raw: val });
      }
      return;
    }
    const text = String(val).trim();
    if (text) output.push({ text, pinned: '', label, raw: val });
  };
  push(source);
  return output;
};

const normalizeListItems = (source) => {
  if (!source) return [];
  if (Array.isArray(source)) {
    return source.filter(Boolean).map((item) => String(item));
  }
  return [String(source)];
};

const normalizeImageItems = (source) => {
  const output = [];
  const push = (val) => {
    if (val == null) return;
    if (Array.isArray(val)) {
      val.forEach((item) => push(item));
      return;
    }
    if (typeof val === 'object') {
      const url = val.URL || val.url || val.image_url || val.image || val.IMAGE_URL || val.IMAGE_ASSET_URL || val.name;
      const name = val.NAME || val.name || val.filename || val.FILE_NAME || val.TITLE || val.title;
      if (url) {
        output.push({ url, name: name || url });
      }
      return;
    }
    const text = String(val).trim();
    if (text) output.push({ url: text, name: text });
  };
  push(source);
  return output;
};

const GoogleCreativeDetails = ({ rowData, reportProfile }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const showCost = resolveShowCost(reportProfile);

  const headlineItems = useMemo(() => {
    return [
      ...normalizeTextItems(rowData?.HEADLINES, 'Headline'),
      ...normalizeTextItems(rowData?.ETA_HEADLINES, 'ETA Headline'),
      ...normalizeTextItems(rowData?.RDA_HEADLINES, 'RDA Headline'),
      ...(rowData?.RDA_LONG_HEADLINE ? [{ text: rowData.RDA_LONG_HEADLINE, pinned: '', label: 'RDA Long Headline' }] : [])
    ].filter(Boolean);
  }, [rowData]);

  const descriptionItems = useMemo(() => {
    return [
      ...normalizeTextItems(rowData?.DESCRIPTIONS, 'Description'),
      ...normalizeTextItems(rowData?.ETA_DESCRIPTIONS, 'ETA Description'),
      ...normalizeTextItems(rowData?.RDA_DESCRIPTIONS, 'RDA Description')
    ].filter(Boolean);
  }, [rowData]);

  const urlItems = useMemo(() => normalizeListItems(rowData?.FINAL_URLS), [rowData]);
  const imageItems = useMemo(() => normalizeImageItems(rowData?.IMAGE_URLS), [rowData]);
  const videoItems = useMemo(() => normalizeListItems(rowData?.VIDEO_IDS), [rowData]);

  const stats = useMemo(() => {
    const impressions = Number(rowData?.IMPRESSIONS ?? rowData?.impressions ?? 0);
    const clicks = Number(rowData?.CLICKS ?? rowData?.clicks ?? 0);
    const conversions = Number(rowData?.CONVERSIONS ?? rowData?.conversions ?? 0);
    const cost = Number(rowData?.COST ?? rowData?.cost ?? 0);
    const revenue = Number(rowData?.REVENUE ?? rowData?.revenue ?? 0);
    const ctr = impressions ? (clicks / impressions) * 100 : 0;
    const convRate = clicks ? (conversions / clicks) * 100 : 0;
    const cpc = clicks ? cost / clicks : 0;
    const roas = cost ? revenue / cost : 0;
    const hasConversions = rowData?.CONVERSIONS != null || rowData?.conversions != null;
    const hasRevenue = rowData?.REVENUE != null || rowData?.revenue != null;
    return { impressions, clicks, conversions, cost, revenue, ctr, convRate, cpc, roas, hasConversions, hasRevenue };
  }, [rowData]);

  const statItems = [
    { label: 'Impr', value: stats.impressions, format: formatNumber },
    { label: 'Clicks', value: stats.clicks, format: formatNumber },
    { label: 'CTR', value: stats.ctr, format: formatPercent },
    ...(stats.hasConversions ? [{ label: 'Conv', value: stats.conversions, format: formatNumber }] : []),
    ...(stats.hasConversions ? [{ label: 'Conv%', value: stats.convRate, format: formatPercent }] : []),
    ...(showCost
      ? [
          { label: 'Spend', value: stats.cost, format: formatMoney },
          { label: 'CPC', value: stats.cpc, format: formatMoney },
          ...(stats.hasRevenue ? [{ label: 'Revenue', value: stats.revenue, format: formatMoney }] : []),
          ...(stats.hasRevenue ? [{ label: 'ROAS', value: stats.roas, format: (val) => `${Number(val || 0).toFixed(2)}x` }] : [])
        ]
      : [])
  ];

  const infoItems = [
    { label: 'Campaign', value: rowData?.CAMPAIGN_NAME },
    { label: 'Ad Group', value: rowData?.ADGROUP_NAME },
    { label: 'Ad ID', value: rowData?.AD_ID },
    { label: 'Status', value: rowData?.STATUS },
    { label: 'Ad Type', value: rowData?.AD_TYPE },
    ...(rowData?.BUSINESS_NAME ? [{ label: 'Business', value: rowData.BUSINESS_NAME }] : []),
    ...(rowData?.CTA ? [{ label: 'CTA', value: rowData.CTA }] : [])
  ].filter((item) => item.value != null && item.value !== '');

  return (
    <Box sx={{ p: 1, background: '#fafafa', display: 'grid', gap: 1 }}>
      <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
        <Card variant="outlined">
          <CardContent sx={{ display: 'grid', gap: 0.75 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Ad Summary
            </Typography>
            <Box sx={{ display: 'grid', gap: 0.5 }}>
              {infoItems.map((item) => (
                <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {item.label}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'right' }}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
        <Card variant="outlined">
          <CardContent sx={{ display: 'grid', gap: 0.75 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Performance
            </Typography>
            <Box sx={{ display: 'grid', gap: 0.5, gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
              {statItems.map((item) => (
                <Box key={item.label} sx={{ display: 'grid', gap: 0.25 }}>
                  <Typography variant="caption" color="text.secondary">
                    {item.label}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {item.format(item.value)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>

      <ResponsiveTabs sx={getAnalyticsReportTabsSx()} value={tabIndex} onChange={(_, val) => setTabIndex(val)}>
        {TABS.map((tab) => (
          <Tab key={tab.key} label={tab.label} />
        ))}
      </ResponsiveTabs>

      {tabIndex === 0 && (
        <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
          {headlineItems.length ? headlineItems.map((item, index) => (
            <Card key={`${item.text}-${index}`} variant="outlined">
              <CardContent sx={{ display: 'grid', gap: 0.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {item.label || 'Headline'} {index + 1}
                  </Typography>
                  {item.pinned ? <Chip size="small" label={`Pinned ${item.pinned}`} /> : null}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {item.text}
                </Typography>
              </CardContent>
            </Card>
          )) : (
            <Typography variant="body2" color="text.secondary">No headlines available.</Typography>
          )}
        </Box>
      )}

      {tabIndex === 1 && (
        <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
          {descriptionItems.length ? descriptionItems.map((item, index) => (
            <Card key={`${item.text}-${index}`} variant="outlined">
              <CardContent sx={{ display: 'grid', gap: 0.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {item.label || 'Description'} {index + 1}
                  </Typography>
                  {item.pinned ? <Chip size="small" label={`Pinned ${item.pinned}`} /> : null}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {item.text}
                </Typography>
              </CardContent>
            </Card>
          )) : (
            <Typography variant="body2" color="text.secondary">No descriptions available.</Typography>
          )}
        </Box>
      )}

      {tabIndex === 2 && (
        <Box sx={{ display: 'grid', gap: 1 }}>
          {urlItems.length ? urlItems.map((url, index) => (
            <Card key={`${url}-${index}`} variant="outlined">
              <CardContent sx={{ display: 'grid', gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary">Final URL {index + 1}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                  {url}
                </Typography>
              </CardContent>
            </Card>
          )) : (
            <Typography variant="body2" color="text.secondary">No final URLs available.</Typography>
          )}
        </Box>
      )}

      {tabIndex === 3 && (
        <Box sx={{ display: 'grid', gap: 1 }}>
          {imageItems.length > 0 && (
            <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' } }}>
              {imageItems.map((item, index) => (
                <Card key={`${item.url}-${index}`} variant="outlined">
                  <CardContent sx={{ display: 'grid', gap: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 120,
                        borderRadius: 1,
                        border: '1px solid rgba(0,0,0,0.08)',
                        background: '#fff',
                        p: 0.5
                      }}
                    >
                      <img
                        style={{ maxWidth: '100%', maxHeight: 140, cursor: 'pointer' }}
                        alt={item.name}
                        src={item.url}
                        onClick={() => {
                          setLightboxImage(item.url);
                          setLightboxOpen(true);
                        }}
                        onError={e => {
                          e.target.src = 'https://api.digitalwerks.com/imageFiles/no-image-icon.png';
                        }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" noWrap title={item.name}>
                      {item.name}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
          {videoItems.length > 0 && (
            <Box sx={{ display: 'grid', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Video IDs
              </Typography>
              <Box sx={{ display: 'grid', gap: 0.5 }}>
                {videoItems.map((video, index) => (
                  <Typography key={`${video}-${index}`} variant="body2" sx={{ fontWeight: 600 }}>
                    {video}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
          {!imageItems.length && !videoItems.length && (
            <Typography variant="body2" color="text.secondary">No image or video assets available.</Typography>
          )}
        </Box>
      )}

      {lightboxOpen && (
        <Box
          onClick={() => setLightboxOpen(false)}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.8)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'zoom-out'
          }}
        >
          <img
            src={lightboxImage}
            alt="Preview"
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              boxShadow: '0 0 20px #000',
              borderRadius: 8,
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default GoogleCreativeDetails;
